import { Location, NavigateFunction } from 'react-router'
import { first } from 'rxjs/operators'
import { CHANGE_PASSWORD_ROUTE_PATH, MFA_ROUTE_PATH, routePath } from '../Constants/Constants'
import { getURLFromRedirectCommand } from '../Constants/ApiConstants'
import { requestGetJSON } from '../../NonReact/common/API/REST/RESTAPIClient'
import { APIElements, AUTH_OUTFLOW_URL } from '../../NonReact/constants/ApiConstants'
import { isStringNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { moveToURL } from '../../NonReact/constants/LocationUtils'
import { redirectToServletWithBranding, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { Branding } from '../../React/Common/API/BrandingAPI'
import { resolveAndGetCurrentUserInfo } from '../../NonReact/common/RxJS/UserInfoState'
import { userInfoErrorState$ } from '../../NonReact/common/RxJS/UserInfoErrorState'
import { ChildRouteID, RouteID } from '../Components/Molecules/Header/Types/Types'
import { AdminUserResponse, JSONResponse } from '../../NonReact/common/Types/Types'
import { EMPTY_STRING } from '../../NonReact/constants/Common'

const outflowRouting = (
    userInfo: Definable<AdminUserResponse>,
    navigate: NavigateFunction,
    location: Location,
    branding: Nullable<Branding>,
    ignorePromptForTerms?: boolean
): void =>
{
    if (userInfo)
    {
        if (!ignorePromptForTerms && userInfo?.Advanced?.PromptForTerms)
        {
            return navigate(routePath(RouteID.TERMS_AND_CONDITIONS), {
                replace: true,
                state: { from: location }
            })
        }

        if (userInfo?.PasswordChgRequired)
        {
            return navigate(CHANGE_PASSWORD_ROUTE_PATH, {
                replace: true,
                state: { from: location }
            })
        }

        if (userInfo?.EnableMFA)
        {
            return navigate(MFA_ROUTE_PATH, {
                replace: true,
                state: { from: location }
            })
        }
    }

    requestGetJSON<{ location: string }>(AUTH_OUTFLOW_URL)
        .then((authOutflow: JSONResponse<{ location: string }>): void => {
            if (isStringNotEmpty(authOutflow?.location)) {
                const trimmedLocation: string = authOutflow.location.trim()

                if (trimmedLocation.toLowerCase().includes('cmd_')) {
                    redirectToServletWithBranding(trimmedLocation, branding?.URLMapping ?? APIElements.AKIXI_2019)
                } else {
                    // Session may store an App v2 segment (e.g. "users"); do not treat it as a servlet command.
                    const routeSegment: string = trimmedLocation.startsWith('/')
                        ? trimmedLocation.slice(1)
                        : trimmedLocation

                    moveToURL(getURLFromRedirectCommand(routeSegment, branding?.URLMapping ?? APIElements.AKIXI_2019))
                }
            }
        })
        .catch((): void =>
        {
            // TODO: These will need to be replaced with navigation to the correct paths when there are completed.
            if (userInfo?.InsightsHubHidden)
            {
                return redirectToServletWithBranding(
                    ServletCmds.RPT_PORTAL,
                    branding?.URLMapping ?? APIElements.AKIXI_2019
                )
            }

            return redirectToServletWithBranding(
                ServletCmds.INSIGHTS_HUB,
                branding?.URLMapping ?? APIElements.AKIXI_2019
            )
        })

    return undefined
}

export const performAuthOutflowRouting = (
    navigate: NavigateFunction,
    location: Location,
    branding?: Nullable<Branding>,
    ignorePromptForTerms?: boolean
): void =>
{
    resolveAndGetCurrentUserInfo().then((userInfo: Definable<AdminUserResponse>): void =>
        outflowRouting(userInfo, navigate, location, branding, ignorePromptForTerms)
    )

    userInfoErrorState$.observable.pipe(first()).subscribe((isError: Definable<boolean>): void =>
    {
        if (isError)
        {
            outflowRouting(undefined, navigate, location, branding, ignorePromptForTerms)
        }
    })
}

export const createRouteURI = (routeID: RouteID, child?: ChildRouteID): string =>
    `/${routeID}${isStringNotEmpty(child) ? `/${child}` : EMPTY_STRING}`
